import ExcelJS from 'exceljs'
import { ColDef } from 'ag-grid-community'
import * as FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'
import { MenuStorage } from '@/utils/storage/MenuStorage'

export class ExcelActions {
  private wb: ExcelJS.Workbook | null = null

  private ws: ExcelJS.Worksheet | null = null

  private excelName = ''

  /** [fileName] 미지정 시 현재 메뉴명을 쓴다. 한 화면이 표를 여러 개 내려받으면 이름이 겹쳐 구분이 필요하다. */
  download = async <T extends object>({ columnDefs, rowData, fileName }: { columnDefs: ColDef<T>[]; rowData?: T[]; fileName?: string }) => {
    if (!rowData?.length) throw new Error('다운로드 할 데이터가 없습니다. 검색 후 다운로드 해주세요.')

    this.setSheet().setExcelName(fileName).addColumns(columnDefs).addRows(rowData)

    const buffer = await this.wb?.xlsx.writeBuffer()
    if (buffer) FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${this.excelName}.xlsx`)
  }

  setSheet = () => {
    this.wb = new ExcelJS.Workbook()
    this.ws = this.wb.addWorksheet('ExcelJS sheet')

    return this
  }

  addColumns = <T extends object>(columnDefs: ColDef<T>[]) => {
    if (!this.ws) return this

    this.ws.columns = columnDefs.map(({ headerName, field }) => ({
      header: headerName,
      key: field,
      width: 10,
    }))

    return this
  }

  addRows = <T extends object>(rowData: T[]) => {
    if (!this.ws) return this

    rowData.forEach((value) => {
      this.ws?.addRow(value)
    })

    return this
  }

  setExcelName = (fileName?: string) => {
    const date = dayjs().format(DATE_FORMAT.dateSimple)
    const menuName = MenuStorage.menuOnSession.getCurrentMenu()?.name || ''
    this.excelName = `${date}_${fileName || menuName}`

    return this
  }
}

const Excel = new ExcelActions()

export default Excel

/** 헤더 행(1행)을 제외한 데이터 행이 없는 빈 엑셀 파일인지 확인한다. */
export async function checkExcelIsEmpty(file: File): Promise<boolean> {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  return wb.worksheets.every((ws) => {
    let hasValue = false
    ws.eachRow({ includeEmpty: false }, (row) => {
      if (row.number === 1) return
      row.eachCell({ includeEmpty: false }, (cell) => {
        const v = cell.value
        if (v !== null && v !== undefined && String(v).trim() !== '') hasValue = true
      })
    })
    return !hasValue
  })
}
